import { ActionIcon } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';

import { DeleteProjectDialog } from 'app/modules/wallforming/components/projects-manager/DeleteProjectDialog';
import { LoadProjectDialog } from 'app/modules/wallforming/components/projects-manager/LoadProjectDialog';
import { ProjectListItem } from 'app/modules/wallforming/components/projects-manager/ProjectListItem';
import { ProjectsDialog } from 'app/modules/wallforming/components/projects-manager/types/projectDialog';

import type { ProjectSummaryResponse } from 'api/models';
import type { ProjectReference } from 'app/modules/wallforming/components/projects-manager/types/projectReference';


const panelWidth = 760;
const panelHeight = 560;

interface ProjectsPanelProps {
    resolvedActiveProject: ProjectReference | null;
    isDirty: boolean;
    isProjectsPending: boolean;
    projects: ProjectSummaryResponse[];
    isSavePending: boolean;
    isLoadPending: boolean;
    isDeletePending: boolean;
    activeDialog: ProjectsDialog;
    loadCandidate: ProjectReference | null;
    deleteCandidate: { id: string; name: string } | null;
    onClose: () => void;
    setActiveDialog: (dialog: ProjectsDialog) => void;
    onOpenDeleteDialog: (candidate: { id: string; name: string }) => void;
    onOpenLoadDialog: (project: ProjectReference) => void;
    onDeleteProject: (id: string, name: string) => void;
    onLoadProject: (candidate: ProjectReference) => void;
    onSaveAndLoadProject: (candidate: ProjectReference) => void;
}

export const ProjectsPanel = ({
    resolvedActiveProject,
    isDirty,
    isProjectsPending,
    projects,
    isSavePending,
    isLoadPending,
    isDeletePending,
    activeDialog,
    loadCandidate,
    deleteCandidate,
    onClose,
    setActiveDialog,
    onOpenDeleteDialog,
    onOpenLoadDialog,
    onDeleteProject,
    onLoadProject,
    onSaveAndLoadProject
}: ProjectsPanelProps) => {
    const { t } = useTranslation();

    return (
        <div
            style={{
                height: '100vh',
                left: 0,
                pointerEvents: 'none',
                position: 'fixed',
                top: 0,
                width: '100vw',
                zIndex: 9999
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
        >
            <Rnd
                bounds="window"
                default={{
                    x: Math.max((window.innerWidth - panelWidth) / 2, 0),
                    y: Math.max((window.innerHeight - panelHeight) / 2, 0),
                    width: panelWidth,
                    height: panelHeight
                }}
                dragHandleClassName="rnd-drag-handle"
                enableResizing={false}
                enableUserSelectHack={false}
                size={{ width: panelWidth, height: panelHeight }}
                style={{ pointerEvents: 'auto' }}
            >
                <div
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        color: '#374151',
                        fontFamily: 'inherit',
                        fontSize: 12,
                        userSelect: 'none',
                        width: panelWidth,
                        height: panelHeight,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative'
                    }}
                >
                    {/* Drag handle header */}
                    <div
                        className="rnd-drag-handle"
                        style={{
                            alignItems: 'center',
                            background: '#f3f4f6',
                            borderBottom: '1px solid #e5e7eb',
                            borderRadius: '6px 6px 0 0',
                            cursor: 'move',
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 10px'
                        }}
                    >
                        <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}>{t('labelProjects')}</span>

                        <div className="flex items-center gap-2">
                            {resolvedActiveProject && (
                                <span
                                    className="max-w-80 truncate text-[11px] text-text-light"
                                    title={resolvedActiveProject.name}
                                >
                                    {t('projectsActiveProject')}: {resolvedActiveProject.name}
                                    {isDirty ? ` ${t('projectsUnsavedMarker')}` : ''}
                                </span>
                            )}
                            <ActionIcon
                                className="action-icon-small bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto icon-sm"
                                onClick={onClose}
                                onPointerDown={(event) => event.stopPropagation()}
                            >
                                close
                            </ActionIcon>
                        </div>
                    </div>

                    {/* Project list */}
                    <div className="overflow-y-auto p-3" style={{ flex: '1 1 auto', minHeight: 0 }}>
                        {isProjectsPending ? (
                            <div className="py-6 text-center text-xs text-text-placeholder">{t('loading')}</div>
                        ) : projects.length === 0 ? (
                            <div className="rounded-sm border border-border-light bg-background-gray-ultra-light p-4 text-sm text-text-placeholder">
                                {t('projectsEmptyState')}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {projects
                                    .slice()
                                    .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime())
                                    .map((project) => (
                                        <ProjectListItem
                                            key={project.id}
                                            isDeletePending={isDeletePending}
                                            isLoadPending={isLoadPending}
                                            isSavePending={isSavePending}
                                            project={project}
                                            onDelete={() => onOpenDeleteDialog({ id: project.id, name: project.name })}
                                            onLoad={() =>
                                                onOpenLoadDialog({
                                                    id: project.id,
                                                    name: project.name,
                                                    description: project.description
                                                })
                                            }
                                        />
                                    ))}
                            </div>
                        )}
                    </div>

                    {/* Load confirmation overlay */}
                    {activeDialog === ProjectsDialog.LOAD && loadCandidate && (
                        <LoadProjectDialog
                            candidate={loadCandidate}
                            hasUnsavedChanges={Boolean(resolvedActiveProject) && isDirty}
                            isLoadPending={isLoadPending}
                            isSavePending={isSavePending}
                            onClose={() => setActiveDialog(ProjectsDialog.NONE)}
                            onLoad={() => void onLoadProject(loadCandidate)}
                            onSaveAndLoad={() => void onSaveAndLoadProject(loadCandidate)}
                        />
                    )}

                    {/* Delete confirmation overlay */}
                    {activeDialog === ProjectsDialog.DELETE && deleteCandidate && (
                        <DeleteProjectDialog
                            isPending={isDeletePending}
                            projectName={deleteCandidate.name}
                            onClose={() => setActiveDialog(ProjectsDialog.NONE)}
                            onDelete={() => void onDeleteProject(deleteCandidate.id, deleteCandidate.name)}
                        />
                    )}
                </div>
            </Rnd>
        </div>
    );
};
